import {
  Account,
  AdminDeviceTableRow,
  Criteria,
  DownloadableData,
  NewUserAccess,
  NewUserAccessPermissionsGroupUserRoleAssignment,
  NewUserTemporaryAccess,
  PaginatedAPIResponse,
  SalesOrder,
  SalesOrdersAccessTableRow,
  TemporaryAccessDashboardTableRow,
  TemporaryAccessSalesOrdersCriteria,
} from '@zspace/types';
import { http } from '../shared/http';
import { filterNullishCriteria } from '../shared/utils';

export interface AdminService {
  fetchDevicesSalesOrders: (
    criteria: Criteria
  ) => Promise<PaginatedAPIResponse<SalesOrder>>;
  fetchDevicesBySalesOrder: (
    criteria: Criteria,
    salesOrderId: string
  ) => Promise<PaginatedAPIResponse<AdminDeviceTableRow>>;
  fetchAccounts: (criteria: Criteria) => Promise<PaginatedAPIResponse<Account>>;
  fetchSalesOrdersByAccount: (
    criteria: Criteria,
    accountId: string
  ) => Promise<PaginatedAPIResponse<AdminDeviceTableRow>>;
  fetchLicenseKeysSalesOrders: (
    criteria: Criteria
  ) => Promise<PaginatedAPIResponse<SalesOrder>>;
  fetchLicenseKeysBySalesOrder: (
    criteria: Criteria,
    salesOrderId: string
  ) => Promise<PaginatedAPIResponse<AdminDeviceTableRow>>;
  fetchLicenseKeysBySalesOrderDownloadableData: (
    salesOrderId: string
  ) => Promise<DownloadableData<string>>;
  fetchZspaceInternalUsersTemporaryCustomerPermissions: (
    criteria: Criteria
  ) => Promise<PaginatedAPIResponse<TemporaryAccessDashboardTableRow>>;
  fetchAvailableSalesOrdersForTemporaryAccessGranting: (
    criteria: TemporaryAccessSalesOrdersCriteria
  ) => Promise<PaginatedAPIResponse<SalesOrdersAccessTableRow>>;
  grantTemporaryAccess: (
    newUserTemporaryAccess: NewUserTemporaryAccess
  ) => Promise<void>;
  validateCustomerAccessGranting: (
    newUserAccess: NewUserAccess
  ) => Promise<NewUserAccessPermissionsGroupUserRoleAssignment[]>;
  grantCustomerAccess: (newUserAccess: NewUserAccess) => void;
  downloadTemporaryAccessAuditLog: () => Promise<DownloadableData<string>>;
}

export class AdminServiceImpl implements AdminService {
  public async fetchDevicesSalesOrders(
    criteria: Criteria
  ): Promise<PaginatedAPIResponse<SalesOrder>> {
    const { data } = await http(
      `/v1/admin/devices/sales-orders?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchDevicesBySalesOrder(
    criteria: Criteria,
    salesOrderId: string
  ): Promise<PaginatedAPIResponse<AdminDeviceTableRow>> {
    const { data } = await http(
      `/v1/admin/devices/sales-orders/${salesOrderId}?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchAccounts(
    criteria: Criteria
  ): Promise<PaginatedAPIResponse<Account>> {
    const { data } = await http(
      `/v1/admin/sales-orders/accounts?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchSalesOrdersByAccount(
    criteria: Criteria,
    accountId: string
  ): Promise<PaginatedAPIResponse<AdminDeviceTableRow>> {
    const { data } = await http(
      `/v1/admin/sales-orders/accounts/${accountId}?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchLicenseKeysSalesOrders(
    criteria: Criteria
  ): Promise<PaginatedAPIResponse<SalesOrder>> {
    const { data } = await http(
      `/v1/admin/license-keys/sales-orders?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchLicenseKeysBySalesOrder(
    criteria: Criteria,
    salesOrderId: string
  ): Promise<PaginatedAPIResponse<AdminDeviceTableRow>> {
    const { data } = await http(
      `/v1/admin/license-keys/sales-orders/${salesOrderId}?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchLicenseKeysBySalesOrderDownloadableData(
    salesOrderId: string
  ): Promise<DownloadableData<string>> {
    const { data } = await http(
      `/v1/admin/license-keys/sales-orders/${salesOrderId}/export`
    );

    return data;
  }

  public async fetchZspaceInternalUsersTemporaryCustomerPermissions(
    criteria: Criteria
  ): Promise<PaginatedAPIResponse<TemporaryAccessDashboardTableRow>> {
    const { data } = await http(
      `/v1/admin/users/temporary-access?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async fetchAvailableSalesOrdersForTemporaryAccessGranting(
    criteria: TemporaryAccessSalesOrdersCriteria
  ): Promise<PaginatedAPIResponse<SalesOrdersAccessTableRow>> {
    const { data } = await http(
      `/v1/admin/users/temporary-access/sales-orders?` +
        new URLSearchParams(criteria as unknown as Record<string, string>)
    );

    return data;
  }

  public async grantTemporaryAccess(
    newUserTemporaryAccess: NewUserTemporaryAccess
  ): Promise<void> {
    const { data } = await http.post(
      '/v1/admin/users/temporary-access/',
      newUserTemporaryAccess
    );

    return data;
  }

  public async validateCustomerAccessGranting(
    newUserAccess: NewUserAccess
  ): Promise<NewUserAccessPermissionsGroupUserRoleAssignment[]> {
    const { data } = await http.post(
      '/v1/admin/users/customer-access/validate',
      newUserAccess
    );

    return data;
  }

  public async grantCustomerAccess(
    newUserAccess: NewUserAccess
  ): Promise<void> {
    const { data } = await http.post(
      '/v1/admin/users/customer-access/',
      newUserAccess
    );

    return data;
  }

  public async downloadTemporaryAccessAuditLog(): Promise<
    DownloadableData<string>
  > {
    const { data } = await http.get<DownloadableData<string>>(
      '/v1/admin/users/temporary-access/audit-log'
    );
    return data;
  }
}

export const fetchDevicesSalesOrders = async (
  criteria: Criteria
): Promise<PaginatedAPIResponse<SalesOrder>> => {
  const service = new AdminServiceImpl();

  return service.fetchDevicesSalesOrders(filterNullishCriteria(criteria));
};

export const fetchDevicesBySalesOrder = async (
  salesOrderId: string,
  criteria: Criteria
): Promise<PaginatedAPIResponse<AdminDeviceTableRow>> => {
  const service = new AdminServiceImpl();

  return service.fetchDevicesBySalesOrder(
    filterNullishCriteria(criteria),
    salesOrderId
  );
};

export const fetchAccounts = async (
  criteria: Criteria
): Promise<PaginatedAPIResponse<Account>> => {
  const service = new AdminServiceImpl();

  return service.fetchAccounts(filterNullishCriteria(criteria));
};

export const fetchSalesOrdersByAccount = async (
  accountId: string,
  criteria: Criteria
): Promise<PaginatedAPIResponse<AdminDeviceTableRow>> => {
  const service = new AdminServiceImpl();

  return service.fetchSalesOrdersByAccount(
    filterNullishCriteria(criteria),
    accountId
  );
};

export const fetchLicenseKeysSalesOrders = async (
  criteria: Criteria
): Promise<PaginatedAPIResponse<SalesOrder>> => {
  const service = new AdminServiceImpl();

  return service.fetchLicenseKeysSalesOrders(filterNullishCriteria(criteria));
};

export const fetchLicenseKeysBySalesOrder = async (
  salesOrderId: string,
  criteria: Criteria
): Promise<PaginatedAPIResponse<AdminDeviceTableRow>> => {
  const service = new AdminServiceImpl();

  return service.fetchLicenseKeysBySalesOrder(
    filterNullishCriteria(criteria),
    salesOrderId
  );
};

export const fetchLicenseKeysBySalesOrderDownloadableData = async (
  salesOrderId: string
): Promise<DownloadableData<string>> => {
  const service = new AdminServiceImpl();

  return service.fetchLicenseKeysBySalesOrderDownloadableData(salesOrderId);
};

export const fetchZspaceInternalUsersTemporaryCustomerPermissions = async (
  criteria: Criteria
): Promise<PaginatedAPIResponse<TemporaryAccessDashboardTableRow>> => {
  const service = new AdminServiceImpl();

  return service.fetchZspaceInternalUsersTemporaryCustomerPermissions(
    filterNullishCriteria(criteria)
  );
};

export const fetchAvailableSalesOrdersForTemporaryAccessGranting = async (
  criteria: TemporaryAccessSalesOrdersCriteria
): Promise<PaginatedAPIResponse<SalesOrdersAccessTableRow>> => {
  const service = new AdminServiceImpl();

  return service.fetchAvailableSalesOrdersForTemporaryAccessGranting(
    filterNullishCriteria(criteria)
  );
};

export const grantTemporaryAccess = async (
  newUserTemporaryAccess: NewUserTemporaryAccess
): Promise<void> => {
  const service = new AdminServiceImpl();

  return service.grantTemporaryAccess(newUserTemporaryAccess);
};

export const validateCustomerAccessGranting = async (
  newUserAccess: NewUserAccess
): Promise<NewUserAccessPermissionsGroupUserRoleAssignment[]> => {
  const service = new AdminServiceImpl();

  return service.validateCustomerAccessGranting(newUserAccess);
};

export const grantCustomerAccess = async (
  newUserAccess: NewUserAccess
): Promise<void> => {
  const service = new AdminServiceImpl();

  return service.grantCustomerAccess(newUserAccess);
};

export const downloadTemporaryAccessAuditLog = async (): Promise<
  DownloadableData<string>
> => {
  const service = new AdminServiceImpl();

  return service.downloadTemporaryAccessAuditLog();
};
